import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  TextField,
  Stack,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Slide,
  Fab,
  Zoom,
  Badge,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useForm } from '@formspree/react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BrushIcon from '@mui/icons-material/Brush';
import SpeedIcon from '@mui/icons-material/Speed';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StarIcon from '@mui/icons-material/Star';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import BuildIcon from '@mui/icons-material/Build';
import ArticleIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import AddIcon from '@mui/icons-material/Add';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import VerifiedIcon from '@mui/icons-material/Verified';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CloseIcon from '@mui/icons-material/Close';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import TimerIcon from '@mui/icons-material/Timer';
import PercentIcon from '@mui/icons-material/Percent';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SecurityIcon from '@mui/icons-material/Security';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import PaletteIcon from '@mui/icons-material/Palette';
import DevicesIcon from '@mui/icons-material/Devices';

const FORMSPREE_ID = 'xwvbpkpb';
const WHATSAPP_NUMBER = '972587489062';

const FloatingParticles = () => (
  <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {[...Array(30)].map((_, i) => (
      <motion.div
        key={i}
        style={{
          position: 'absolute',
          width: Math.random() * 6 + 2,
          height: Math.random() * 6 + 2,
          borderRadius: '50%',
          background: `rgba(255, 215, 0, ${Math.random() * 0.5 + 0.1})`,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{ 
          y: [0, -50, 0], 
          x: [0, Math.random() * 20 - 10, 0],
          opacity: [0.2, 0.8, 0.2],
          scale: [1, 1.2, 1],
        }}
        transition={{ 
          duration: Math.random() * 5 + 4, 
          repeat: Infinity, 
          ease: 'easeInOut', 
          delay: Math.random() * 3 
        }}
      />
    ))}
  </Box>
);

const AnimatedCounter = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { 
      if (entry.isIntersecting) setIsVisible(true); 
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const GlowText = ({ children }) => (
  <Box component="span" sx={{ 
    color: '#FFD700', 
    textShadow: '0 0 30px rgba(255,215,0,0.5), 0 0 60px rgba(255,215,0,0.3)',
  }}>
    {children}
  </Box>
);

const LiveActivityPopup = ({ show, name, action }) => (
  <Slide direction="up" in={show} mountOnEnter unmountOnExit>
    <Box sx={{
      position: 'fixed',
      bottom: 100,
      left: 20,
      background: 'linear-gradient(135deg, rgba(0,0,0,0.95), rgba(30,30,30,0.95))',
      border: '1px solid rgba(255,215,0,0.3)',
      borderRadius: 3,
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      zIndex: 1000,
      boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
      maxWidth: 300,
    }}>
      <Box sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #FFD700, #FFA500)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <GroupsIcon sx={{ color: '#000', fontSize: 20 }} />
      </Box>
      <Box>
        <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>{name}</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>{action}</Typography>
      </Box>
    </Box>
  </Slide>
);

const UrgencyBanner = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 59, seconds: 59 });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return { hours: 23, minutes: 59, seconds: 59 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box sx={{
      background: 'linear-gradient(90deg, #FFD700, #FFA500, #FFD700)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 3s linear infinite',
      '@keyframes shimmer': { '0%': { backgroundPosition: '200% 0' }, '100%': { backgroundPosition: '-200% 0' } },
      py: 1.5,
      textAlign: 'center',
    }}>
      <Container maxWidth="lg">
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" flexWrap="wrap">
          <LocalFireDepartmentIcon sx={{ color: '#000' }} />
          <Typography sx={{ color: '#000', fontWeight: 700, fontSize: { xs: '0.85rem', md: '1rem' } }}>
            🔥 מבצע מיוחד! עוד {timeLeft.hours}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')} לסיום
          </Typography>
          <Chip label="חסכו 50₪" size="small" sx={{ background: '#000', color: '#FFD700', fontWeight: 700 }} />
        </Stack>
      </Container>
    </Box>
  );
};

const TOSDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { background: '#0a0a0a', border: '1px solid rgba(255,215,0,0.2)', borderRadius: 3 } }}>
    <DialogTitle sx={{ borderBottom: '1px solid rgba(255,215,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFD700' }}>תקנון ותנאי שימוש</Typography>
      <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent sx={{ mt: 2 }}>
      <Box sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 2 }}>
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, fontWeight: 700 }}>1. כללי</Typography>
        <Typography paragraph>ברוכים הבאים ל-Diamond Landing. תקנון זה מסדיר את תנאי השימוש בשירותים שלנו. השימוש בשירותים מהווה הסכמה לתנאים אלו.</Typography>
        
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, mt: 3, fontWeight: 700 }}>2. השירותים</Typography>
        <Typography paragraph>Diamond Landing מספקת שירותי עיצוב ובניית דפי נחיתה. השירות הבסיסי (₪150) כולל:</Typography>
        <Box component="ul" sx={{ mb: 2, pl: 3 }}>
          <li>שיחת אפיון אישית</li>
          <li>כתיבת קופי מקצועי</li>
          <li>עיצוב דף נחיתה</li>
          <li>התאמה מלאה למובייל</li>
          <li>סבב תיקונים אחד (עד 3 שינויים)</li>
        </Box>
        
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, mt: 3, fontWeight: 700 }}>3. תשלום</Typography>
        <Typography paragraph>התשלום מתבצע מראש לפני תחילת העבודה. המחיר הבסיסי הוא ₪150. תוספות ושירותים נוספים יתומחרו בנפרד לפי מחירון התוספות.</Typography>
        
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, mt: 3, fontWeight: 700 }}>4. זמני אספקה</Typography>
        <Typography paragraph>זמן האספקה הסטנדרטי הוא 48 שעות עסקיות מרגע קבלת כל החומרים הנדרשים. פרויקטים דחופים (24 שעות) זמינים בתוספת ₪100.</Typography>
        
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, mt: 3, fontWeight: 700 }}>5. תיקונים ושינויים ⚠️</Typography>
        <Box sx={{ background: 'rgba(255,152,0,0.1)', border: '1px solid rgba(255,152,0,0.3)', borderRadius: 2, p: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>חשוב לקרוא!</Typography>
          <Typography paragraph sx={{ mb: 1 }}>• החבילה הבסיסית כוללת <strong>סבב תיקונים אחד בלבד</strong>.</Typography>
          <Typography paragraph sx={{ mb: 1 }}>• סבב תיקונים מוגבל ל<strong>עד 3 שינויים</strong> (שינוי צבע/טקסט/תמונה/מיקום = שינוי אחד).</Typography>
          <Typography paragraph sx={{ mb: 1 }}>• שינויים מבניים (שינוי מבנה העמוד, הוספת סקשנים) אינם נחשבים כתיקון אלא כעבודה נוספת.</Typography>
          <Typography paragraph sx={{ mb: 1 }}>• סבב תיקונים נוסף: <strong>₪100</strong> (עד 3 שינויים).</Typography>
          <Typography paragraph sx={{ mb: 0 }}>• שינויים בודדים מעבר לסבבים: <strong>₪80 לשעת עבודה</strong>.</Typography>
        </Box>
        
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, mt: 3, fontWeight: 700 }}>6. תוספות ומחירון</Typography>
        <Box sx={{ background: 'rgba(255,215,0,0.05)', borderRadius: 2, p: 2, mb: 2 }}>
          <Typography paragraph sx={{ mb: 1 }}>• דומיין לשנה (.co.il / .com): <strong>₪80</strong></Typography>
          <Typography paragraph sx={{ mb: 1 }}>• אחסון שנתי: <strong>₪150</strong></Typography>
          <Typography paragraph sx={{ mb: 1 }}>• טופס לידים פעיל: <strong>₪80</strong></Typography>
          <Typography paragraph sx={{ mb: 1 }}>• עמוד נוסף (אודות/שירותים): <strong>₪150</strong></Typography>
          <Typography paragraph sx={{ mb: 1 }}>• פיקסל פייסבוק: <strong>₪80</strong></Typography>
          <Typography paragraph sx={{ mb: 1 }}>• שינויים עתידיים: <strong>₪80 לשעה</strong></Typography>
          <Typography paragraph sx={{ mb: 0 }}>• דחיפות (24 שעות): <strong>₪100</strong></Typography>
        </Box>
        
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, mt: 3, fontWeight: 700 }}>7. קניין רוחני</Typography>
        <Typography paragraph>לאחר השלמת התשלום המלא, זכויות השימוש בדף הנחיתה עוברות ללקוח. Diamond Landing שומרת על הזכות להציג את העבודה בפורטפוליו.</Typography>
        
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, mt: 3, fontWeight: 700 }}>8. ביטולים</Typography>
        <Typography paragraph>ביטול לפני תחילת העבודה: ניתן לבטל ללא חיוב. לאחר תחילת העבודה או קבלת טיוטה ראשונה: התשלום אינו ניתן להחזר. מומלץ לוודא את כל הפרטים לפני תחילת הפרויקט.</Typography>
        
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, mt: 3, fontWeight: 700 }}>9. פרטיות</Typography>
        <Typography paragraph>אנו מתחייבים לשמור על פרטיות המידע שנמסר לנו. המידע ישמש אך ורק לצורך מתן השירות ולא יועבר לצדדים שלישיים.</Typography>
        
        <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, mt: 3, fontWeight: 700 }}>10. יצירת קשר</Typography>
        <Typography paragraph>לכל שאלה או בירור ניתן לפנות אלינו בוואטסאפ: 058-748-9062</Typography>
        
        <Typography sx={{ mt: 4, color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>עדכון אחרון: מרץ 2026</Typography>
      </Box>
    </DialogContent>
    <DialogActions sx={{ borderTop: '1px solid rgba(255,215,0,0.1)', p: 2 }}>
      <Button onClick={onClose} sx={{ color: '#FFD700' }}>סגור</Button>
    </DialogActions>
  </Dialog>
);

const AccessibilityDialog = ({ open, onClose }) => {
  const [fontSize, setFontSize] = useState(100);
  const [highContrast, setHighContrast] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { background: highContrast ? '#000' : '#0a0a0a', border: `2px solid ${highContrast ? '#FFD700' : 'rgba(255,215,0,0.2)'}`, borderRadius: 3 } }}>
      <DialogTitle sx={{ borderBottom: '1px solid rgba(255,215,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AccessibilityNewIcon sx={{ color: '#FFD700' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFD700' }}>הגדרות נגישות</Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ mt: 2, minWidth: 300 }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: '#fff', mb: 1, fontWeight: 600 }}>גודל טקסט</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setFontSize(Math.max(80, fontSize - 10))} sx={{ borderColor: '#FFD700', color: '#FFD700' }}>A-</Button>
            <Button variant="outlined" onClick={() => setFontSize(100)} sx={{ borderColor: '#FFD700', color: '#FFD700' }}>איפוס</Button>
            <Button variant="outlined" onClick={() => setFontSize(Math.min(150, fontSize + 10))} sx={{ borderColor: '#FFD700', color: '#FFD700' }}>A+</Button>
          </Stack>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', mt: 1, fontSize: '0.85rem' }}>גודל נוכחי: {fontSize}%</Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,215,0,0.1)', my: 2 }} />
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: '#fff', mb: 1, fontWeight: 600 }}>ניגודיות גבוהה</Typography>
          <Button variant={highContrast ? "contained" : "outlined"} onClick={() => setHighContrast(!highContrast)} sx={{ borderColor: '#FFD700', color: highContrast ? '#000' : '#FFD700', background: highContrast ? '#FFD700' : 'transparent' }}>{highContrast ? 'מופעל' : 'כבוי'}</Button>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,215,0,0.1)', my: 2 }} />
        <Box>
          <Typography sx={{ color: '#fff', mb: 1, fontWeight: 600 }}>קיצורי מקלדת</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>• Tab - מעבר בין אלמנטים<br />• Enter - הפעלת כפתור<br />• Esc - סגירת חלונות</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(255,215,0,0.1)', p: 2 }}>
        <Button onClick={onClose} sx={{ color: '#FFD700' }}>סגור</Button>
      </DialogActions>
    </Dialog>
  );
};

const ExitIntentPopup = ({ open, onClose, onClaim }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" PaperProps={{ sx: { background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)', border: '2px solid rgba(255,215,0,0.4)', borderRadius: 4, overflow: 'visible' } }}>
    <Box sx={{ position: 'absolute', top: -40, left: '50%', transform: 'translateX(-50%)', width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #FFD700, #FFA500)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 40px rgba(255,215,0,0.4)' }}>
      <CardGiftcardIcon sx={{ fontSize: 40, color: '#000' }} />
    </Box>
    <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8, color: '#fff' }}><CloseIcon /></IconButton>
    <DialogContent sx={{ textAlign: 'center', pt: 8, pb: 4, px: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 2 }}>רגע! לפני שאתה הולך... 🎁</Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, fontSize: '1.1rem' }}>קבל <Box component="span" sx={{ color: '#FFD700', fontWeight: 700 }}>50₪ הנחה</Box> על דף הנחיתה הראשון שלך!</Typography>
      <Typography sx={{ color: '#FFD700', fontWeight: 700, fontSize: '2rem', mb: 3, textShadow: '0 0 30px rgba(255,215,0,0.5)' }}>רק ₪100 במקום ₪150</Typography>
      <Button variant="contained" size="large" fullWidth onClick={onClaim} sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#000', py: 2, fontSize: '1.2rem', fontWeight: 800, borderRadius: 3, '&:hover': { background: 'linear-gradient(135deg, #FFE44D, #FFB732)' } }}>🚀 אני רוצה את ההנחה!</Button>
      <Typography sx={{ color: 'rgba(255,255,255,0.4)', mt: 2, fontSize: '0.85rem' }}>* ההצעה בתוקף ל-24 שעות בלבד</Typography>
    </DialogContent>
  </Dialog>
);

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formspreeState, handleFormspreeSubmit] = useForm(FORMSPREE_ID);
  const [formData, setFormData] = useState({ name: '', phone: '', business: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [tosOpen, setTosOpen] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const [exitPopupOpen, setExitPopupOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [liveActivity, setLiveActivity] = useState({ show: false, name: '', action: '' });
  const [welcomeSnackbar, setWelcomeSnackbar] = useState(true);
  const exitIntentShown = useRef(false);

  useEffect(() => {
    const names = ['משה כ.', 'יעל מ.', 'דני ר.', 'שירה ב.', 'אבי ל.', 'נועה ג.'];
    const actions = ['הזמין דף נחיתה לפני 2 דקות', 'קיבל את הדף שלו עכשיו!', 'השאיר פרטים לפני 5 דקות', 'סגר עסקה עכשיו!', 'מרוצה מהתוצאה! ⭐⭐⭐⭐⭐'];
    const showActivity = () => {
      const name = names[Math.floor(Math.random() * names.length)];
      const action = actions[Math.floor(Math.random() * actions.length)];
      setLiveActivity({ show: true, name, action });
      setTimeout(() => setLiveActivity({ show: false, name: '', action: '' }), 4000);
    };
    const interval = setInterval(showActivity, 15000);
    setTimeout(showActivity, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY < 10 && !exitIntentShown.current) {
        exitIntentShown.current = true;
        setExitPopupOpen(true);
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

    useEffect(() => {
        if (formspreeState.succeeded) {
            setShowSuccess(true);
            setFormData({ name: '', phone: '', business: '' });
            // Meta Pixel - Track Lead
            if (typeof window.fbq === 'function') {
                window.fbq('track', 'Lead');
            }
        }
    }, [formspreeState.succeeded]);

  const handleWhatsApp = (msg = 'היי! אני מעוניין בדף נחיתה מקצועי 💎') => {
    window.open(`https://api.whatsapp.com/send/?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(msg)}`, '_blank');
  };

  const scrollToForm = () => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const included = [
    { icon: <SupportAgentIcon />, title: 'שיחת אפיון אישית', desc: 'נבין יחד את העסק והצרכים שלך' },
    { icon: <EditNoteIcon />, title: 'קופי שמוכר', desc: 'טקסטים שכתובים למכור ומניעים לפעולה' },
    { icon: <PaletteIcon />, title: 'עיצוב פרימיום', desc: 'עיצוב יוקרתי שנראה כמו עשרות אלפים' },
    { icon: <DevicesIcon />, title: 'מותאם לכל מסך', desc: 'מושלם בנייד, טאבלט ומחשב' },
    { icon: <SpeedIcon />, title: 'מהיר ומאובטח', desc: 'טכנולוגיה מתקדמת וביצועים גבוהים' },
    { icon: <AutoAwesomeIcon />, title: 'סבב תיקונים', desc: 'עד 3 שינויים לשביעות רצונך' },
  ];

  const addons = [
    { name: 'דומיין לשנה', price: '80', icon: <LanguageIcon />, desc: '.co.il או .com' },
    { name: 'אחסון שנתי', price: '150', icon: <StorageIcon />, desc: 'מהיר ומאובטח' },
    { name: 'טופס פעיל', price: '80', icon: <ArticleIcon />, desc: 'לידים ישירות למייל' },
    { name: 'עמוד נוסף', price: '150', icon: <AddIcon />, desc: 'אודות/שירותים' },
    { name: 'פיקסל פייסבוק', price: '80', icon: <CampaignIcon />, desc: 'לרימרקטינג' },
    { name: 'סבב תיקונים נוסף', price: '100', icon: <BuildIcon />, desc: 'עד 3 שינויים' },
    { name: 'שינויים עתידיים', price: '80/שעה', icon: <BuildIcon />, desc: 'עדכונים ותוספות' },
  ];

  const stats = [
    { value: 127, suffix: '+', label: 'לקוחות מרוצים', icon: <GroupsIcon /> },
    { value: 48, suffix: ' שעות', label: 'זמן אספקה ממוצע', icon: <TimerIcon /> },
    { value: 5, suffix: ' שנים', label: 'ניסיון בתחום', icon: <ThumbUpIcon /> },
    { value: 3, suffix: 'X', label: 'יותר לידים', icon: <TrendingUpIcon /> },
  ];

  const testimonials = [
    { name: 'יוסי כהן', business: 'קבלן שיפוצים', text: 'תוך יומיים היה לי דף שמביא לידים איכותיים כל יום. ההשקעה הכי משתלמת שעשיתי לעסק!', rating: 5, image: '👨‍🔧' },
    { name: 'מיכל רוזנברג', business: 'מאמנת כושר', text: 'לא האמנתי שב-150 שקל אפשר לקבל משהו כזה יפה ומקצועי. הלקוחות שואלים מי עיצב לי!', rating: 5, image: '👩‍🦰' },
    { name: 'אבי דוידוב', business: 'סוכן ביטוח', text: 'הדף הזה הכפיל לי את הלידים מהפייסבוק. תוך שבוע כבר החזרתי את ההשקעה פי 10!', rating: 5, image: '👨‍💼' },
    { name: 'שירה לוי', business: 'מעצבת פנים', text: 'שירות מדהים, מקצועיות ברמה הגבוהה ביותר. ממליצה לכל בעל עסק!', rating: 5, image: '👩‍🎨' },
  ];

  const whyUs = [
    { icon: <RocketLaunchIcon />, title: 'מהיר', desc: 'הדף שלך מוכן תוך 24-48 שעות' },
    { icon: <SecurityIcon />, title: 'שירות אישי', desc: 'זמין לשאלות ושינויים' },
    { icon: <Diversity3Icon />, title: 'יחס אישי', desc: 'לא סוכנות - אדם אחד שמכיר אותך' },
    { icon: <PercentIcon />, title: 'מחיר הוגן', desc: 'איכות של אלפי שקלים במחיר נגיש' },
  ];

  const faqs = [
    { q: 'מה בדיוק כלול ב-150₪?', a: 'שיחת אפיון אישית, קופי מקצועי שכתוב למכור, עיצוב פרימיום שנראה כמו אלפי שקלים, התאמה מלאה לנייד וטאבלט, וסבב תיקונים אחד (עד 3 שינויים). לא כולל דומיין ואחסון (תוספות אופציונליות).' },
    { q: 'כמה זמן עד שהדף שלי מוכן?', a: 'ברוב המקרים 48 שעות עסקיות מרגע שאני מקבל את כל החומרים. אם יש לך דדליין דחוף, יש אפשרות לשירות אקספרס (24 שעות) תמורת ₪100 נוספים.' },
    { q: 'מה אם אני לא מרוצה מהתוצאה?', a: 'יש לך סבב תיקונים אחד הכולל עד 3 שינויים. רוב הלקוחות מרוצים כבר אחרי הסבב הראשון. אם צריך עוד שינויים - ניתן לרכוש סבב נוסף ב-₪100.' },
    { q: 'מה זה "עד 3 שינויים" בסבב תיקונים?', a: 'שינוי = תיקון אחד. לדוגמה: שינוי צבע, החלפת טקסט, החלפת תמונה, שינוי מיקום אלמנט. 3 דברים כאלה = סבב אחד. שינויים מבניים (הוספת סקשנים) נחשבים לעבודה נוספת.' },
    { q: 'אני לא מבין בטכנולוגיה, זה בעיה?', a: 'בכלל לא! אני מטפל בהכל מא\' ועד ת\'. אתה רק צריך לספר לי על העסק שלך, ואני דואג לכל השאר.' },
    { q: 'איך הדף עולה לאוויר?', a: 'אני נותן לך קבצים מוכנים שאפשר להעלות לכל שרת. אם אתה מעדיף שאני אטפל בזה, יש אפשרות לדומיין (₪80) ואחסון (₪150).' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: '#000', overflow: 'hidden' }}>
      {/* Scroll Progress Bar */}
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'linear-gradient(90deg, #FFD700, #FFA500)',
          transformOrigin: '0%',
          zIndex: 9999,
        }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.1 }}
      />
      
      <UrgencyBanner />
      <LiveActivityPopup {...liveActivity} />
      
      {/* Floating Side Elements */}
      <Box sx={{ 
        position: 'fixed', 
        left: 20, 
        top: '50%', 
        transform: 'translateY(-50%)', 
        zIndex: 100,
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        gap: 2,
      }}>
        {['⭐', '🚀', '💎'].map((emoji, i) => (
          <motion.div 
            key={i}
            animate={{ y: [0, -10, 0], opacity: [0.3, 0.7, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
          >
            <Typography sx={{ fontSize: '1.5rem' }}>{emoji}</Typography>
          </motion.div>
        ))}
      </Box>
      
      <Snackbar open={welcomeSnackbar} autoHideDuration={6000} onClose={() => setWelcomeSnackbar(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setWelcomeSnackbar(false)} severity="info" icon={<NotificationsActiveIcon />} sx={{ background: 'linear-gradient(135deg, #1a1a1a, #2a2a2a)', border: '1px solid rgba(255,215,0,0.3)', color: '#fff' }}>
          <Typography sx={{ fontWeight: 600 }}>ברוכים הבאים! 👋</Typography>
          <Typography sx={{ fontSize: '0.85rem', opacity: 0.8 }}>יש לנו מבצע מיוחד היום - 50₪ הנחה!</Typography>
        </Alert>
      </Snackbar>

      <Zoom in={showScrollTop}>
        <Fab onClick={scrollToTop} sx={{ position: 'fixed', bottom: 100, right: 20, background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#000', '&:hover': { background: '#FFE44D' }, zIndex: 1000 }}><KeyboardArrowUpIcon /></Fab>
      </Zoom>

      <Fab onClick={() => setAccessibilityOpen(true)} size="medium" aria-label="הגדרות נגישות" sx={{ position: 'fixed', bottom: 170, right: 20, background: '#1a1a1a', border: '2px solid #FFD700', color: '#FFD700', '&:hover': { background: '#2a2a2a' }, zIndex: 1000 }}><AccessibilityNewIcon /></Fab>

      <Fab onClick={() => handleWhatsApp()} sx={{ position: 'fixed', bottom: 20, right: 20, background: 'linear-gradient(135deg, #25D366, #128C7E)', color: '#fff', width: 60, height: 60, '&:hover': { background: '#25D366', transform: 'scale(1.1)' }, transition: 'all 0.3s', zIndex: 1000 }}>
        <Badge badgeContent="1" color="error"><WhatsAppIcon sx={{ fontSize: 30 }} /></Badge>
      </Fab>

      <TOSDialog open={tosOpen} onClose={() => setTosOpen(false)} />
      <AccessibilityDialog open={accessibilityOpen} onClose={() => setAccessibilityOpen(false)} />
      <ExitIntentPopup open={exitPopupOpen} onClose={() => setExitPopupOpen(false)} onClaim={() => { setExitPopupOpen(false); scrollToForm(); }} />

      {/* HERO */}
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', background: 'radial-gradient(ellipse at center, #111 0%, #000 70%)', pt: { xs: 4, md: 0 } }}>
        <motion.div animate={{ background: ['radial-gradient(ellipse at 20% 50%, rgba(255,215,0,0.1) 0%, transparent 50%)', 'radial-gradient(ellipse at 80% 50%, rgba(255,215,0,0.1) 0%, transparent 50%)', 'radial-gradient(ellipse at 50% 80%, rgba(255,215,0,0.1) 0%, transparent 50%)', 'radial-gradient(ellipse at 20% 50%, rgba(255,215,0,0.1) 0%, transparent 50%)'] }} transition={{ duration: 10, repeat: Infinity, ease: 'linear' }} style={{ position: 'absolute', inset: 0 }} />
        <FloatingParticles />
        
        <Box sx={{ position: 'absolute', top: 20, left: 20, width: 50, height: 50, borderTop: '2px solid rgba(255,215,0,0.5)', borderLeft: '2px solid rgba(255,215,0,0.5)' }} />
        <Box sx={{ position: 'absolute', top: 20, right: 20, width: 50, height: 50, borderTop: '2px solid rgba(255,215,0,0.5)', borderRight: '2px solid rgba(255,215,0,0.5)' }} />
        <Box sx={{ position: 'absolute', bottom: 20, left: 20, width: 50, height: 50, borderBottom: '2px solid rgba(255,215,0,0.5)', borderLeft: '2px solid rgba(255,215,0,0.5)' }} />
        <Box sx={{ position: 'absolute', bottom: 20, right: 20, width: 50, height: 50, borderBottom: '2px solid rgba(255,215,0,0.5)', borderRight: '2px solid rgba(255,215,0,0.5)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center', py: 4 }}>
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}>
              <Chip icon={<WorkspacePremiumIcon sx={{ color: '#FFD700 !important' }} />} label="המומחים לדפי נחיתה בישראל" sx={{ mb: 4, py: 3, px: 2, background: 'linear-gradient(135deg, rgba(255,215,0,0.2) 0%, transparent 100%)', border: '1px solid rgba(255,215,0,0.5)', color: '#FFD700', fontWeight: 700, fontSize: { xs: '0.8rem', md: '0.95rem' } }} />
            </motion.div>

            <Typography variant="h1" sx={{ fontSize: { xs: '2.2rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' }, fontWeight: 900, lineHeight: 1.1, mb: 3, color: '#fff' }}>
              דף נחיתה שמביא<br />
              <motion.span animate={{ textShadow: ['0 0 20px rgba(255,215,0,0.3)', '0 0 60px rgba(255,215,0,0.7)', '0 0 20px rgba(255,215,0,0.3)'] }} transition={{ duration: 2, repeat: Infinity }} style={{ color: '#FFD700', display: 'inline-block' }}>לקוחות משלמים</motion.span>
            </Typography>

            <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, fontSize: { xs: '1.1rem', md: '1.4rem' }, maxWidth: 650, mx: 'auto', lineHeight: 1.8 }}>עיצוב ברמה הגבוהה ביותר • קופי שמניע לפעולה • מותאם לכל מסך</Typography>
            
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 4 }}>
              <Typography sx={{ color: '#FFD700', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, textShadow: '0 0 30px rgba(255,215,0,0.5)' }}>רק ₪150</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>₪350</Typography>
              <Chip label="57% הנחה!" size="small" sx={{ background: '#ff4444', color: '#fff', fontWeight: 700, animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.05)' } } }} />
            </Stack>

            <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 5, fontSize: { xs: '0.9rem', md: '1rem' } }}>⚡ מוכן תוך 48 שעות | 🎯 איכות מקצועית | 💎 תוצאות אמיתיות</Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 4 }}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="contained" size="large" onClick={scrollToForm} sx={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#000', px: { xs: 5, md: 7 }, py: { xs: 2, md: 2.5 }, fontSize: { xs: '1.1rem', md: '1.4rem' }, fontWeight: 800, borderRadius: 3, boxShadow: '0 10px 50px rgba(255,215,0,0.4)', '&:hover': { background: 'linear-gradient(135deg, #FFE44D 0%, #FFB732 100%)', boxShadow: '0 15px 60px rgba(255,215,0,0.6)' } }}>🚀 רוצה דף נחיתה שמוכר</Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outlined" size="large" startIcon={<WhatsAppIcon />} onClick={() => handleWhatsApp()} sx={{ borderColor: '#FFD700', color: '#FFD700', px: { xs: 4, md: 5 }, py: { xs: 2, md: 2.5 }, fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 600, borderRadius: 3, borderWidth: 2, '&:hover': { borderColor: '#FFD700', background: 'rgba(255,215,0,0.1)', borderWidth: 2 } }}>שיחת ייעוץ חינם</Button>
              </motion.div>
            </Stack>

            <Stack direction="row" spacing={{ xs: 1, md: 3 }} justifyContent="center" flexWrap="wrap" sx={{ mb: 4 }}>
              {[{ icon: '⭐', text: '127+ לקוחות' }, { icon: '✅', text: 'אספקה מהירה' }, { icon: '🔒', text: 'מקצועיות מוכחת' }].map((item, i) => (
                <Stack key={i} direction="row" spacing={0.5} alignItems="center">
                  <Typography sx={{ fontSize: '1.2rem' }}>{item.icon}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.8rem', md: '0.95rem' } }}>{item.text}</Typography>
                </Stack>
              ))}
            </Stack>
          </motion.div>

          <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} style={{ position: 'absolute', bottom: 30, left: '50%', transform: 'translateX(-50%)' }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 40, color: 'rgba(255,215,0,0.4)' }} />
          </motion.div>
        </Container>
      </Box>

      {/* STATS - Premium */}
      <Box sx={{ py: { xs: 10, md: 14 }, background: 'linear-gradient(180deg, #030303 0%, #0a0a0a 100%)', position: 'relative', overflow: 'hidden' }}>
        {/* Animated lines */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.3), transparent)' }} />
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.3), transparent)' }} />
        
        {/* Floating particles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              width: 4,
              height: 4,
              borderRadius: '50%',
              background: '#FFD700',
              left: `${10 + i * 12}%`,
              top: '50%',
            }}
            animate={{ 
              y: [-20, 20, -20], 
              opacity: [0.2, 0.6, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{ duration: 3 + i * 0.5, repeat: Infinity, delay: i * 0.3 }}
          />
        ))}
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Typography variant="h4" sx={{ textAlign: 'center', mb: 6, color: 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: { xs: '1rem', md: '1.2rem' }, letterSpacing: 3 }}>
              📊 המספרים מדברים בעד עצמם
            </Typography>
          </motion.div>
          
          <Grid container spacing={4}>
            {stats.map((s, i) => (
              <Grid item xs={6} md={3} key={i}>
                <motion.div 
                  initial={{ opacity: 0, y: 50, scale: 0.8 }} 
                  whileInView={{ opacity: 1, y: 0, scale: 1 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: i * 0.15, type: 'spring', stiffness: 100 }}
                  whileHover={{ y: -10, scale: 1.05 }}
                >
                  <Card sx={{ 
                    background: 'linear-gradient(145deg, rgba(255,215,0,0.12) 0%, rgba(0,0,0,0.6) 100%)', 
                    border: '1px solid rgba(255,215,0,0.2)', 
                    borderRadius: 5, 
                    p: { xs: 3, md: 4 }, 
                    textAlign: 'center', 
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      border: '1px solid rgba(255,215,0,0.5)',
                      boxShadow: '0 25px 60px rgba(255,215,0,0.2)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: 'linear-gradient(90deg, transparent, #FFD700, transparent)',
                    }
                  }}>
                    {/* Glow background */}
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,215,0,0.1)', filter: 'blur(30px)' }} />
                    
                    <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}>
                      <Box sx={{ 
                        color: '#FFD700', 
                        mb: 2, 
                        width: 70, 
                        height: 70, 
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,215,0,0.05))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        boxShadow: '0 10px 30px rgba(255,215,0,0.2)',
                        '& svg': { fontSize: { xs: 32, md: 38 } } 
                      }}>{s.icon}</Box>
                    </motion.div>
                    
                    <Typography sx={{ 
                      fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.5rem' }, 
                      fontWeight: 900, 
                      background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      textShadow: '0 0 40px rgba(255,215,0,0.3)',
                      position: 'relative',
                      zIndex: 1,
                    }}>
                      <AnimatedCounter end={s.value} suffix={s.suffix} />
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.85rem', md: '1rem' }, fontWeight: 600, mt: 1 }}>{s.label}</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* WHY US - Premium */}
      <Box sx={{ py: { xs: 10, md: 16 }, position: 'relative', overflow: 'hidden' }}>
        {/* Animated background */}
        <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 20% 50%, rgba(255,215,0,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,215,0,0.05) 0%, transparent 50%)' }} />
        <Box sx={{ position: 'absolute', top: '20%', left: '-10%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,215,0,0.03) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <motion.div animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 2, repeat: Infinity }}>
                <Typography sx={{ fontSize: { xs: '3rem', md: '4rem' }, mb: 2 }}>🏆</Typography>
              </motion.div>
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' }, fontWeight: 900, color: '#fff', mb: 2 }}>
                למה <Box component="span" sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>בוחרים בנו?</Box>
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '1rem', md: '1.2rem' }, maxWidth: 600, mx: 'auto' }}>
                5 שנים, מאות לקוחות מרוצים, ותוצאות שמדברות בעד עצמן
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {whyUs.map((item, i) => (
              <Grid item xs={6} md={3} key={i}>
                <motion.div 
                  initial={{ opacity: 0, y: 50, rotateX: -15 }} 
                  whileInView={{ opacity: 1, y: 0, rotateX: 0 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: i * 0.15, duration: 0.6 }}
                  whileHover={{ y: -15, scale: 1.05 }}
                >
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(145deg, rgba(255,215,0,0.08) 0%, rgba(0,0,0,0.4) 100%)', 
                    border: '1px solid rgba(255,215,0,0.2)', 
                    borderRadius: 5, 
                    p: { xs: 3, md: 4 }, 
                    textAlign: 'center', 
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
                    '&:hover': { 
                      background: 'linear-gradient(145deg, rgba(255,215,0,0.15) 0%, rgba(0,0,0,0.6) 100%)', 
                      border: '1px solid rgba(255,215,0,0.6)',
                      boxShadow: '0 25px 50px rgba(255,215,0,0.2), inset 0 1px 0 rgba(255,215,0,0.3)'
                    } 
                  }}>
                    {/* Glow effect */}
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,215,0,0.1)', filter: 'blur(30px)' }} />
                    
                    <Box sx={{ 
                      width: { xs: 60, md: 80 }, 
                      height: { xs: 60, md: 80 }, 
                      borderRadius: 4, 
                      background: 'linear-gradient(135deg, rgba(255,215,0,0.3), rgba(255,215,0,0.1))', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      mx: 'auto', 
                      mb: 3,
                      boxShadow: '0 10px 30px rgba(255,215,0,0.2)',
                      '& svg': { fontSize: { xs: 28, md: 36 }, color: '#FFD700' } 
                    }}>{item.icon}</Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', md: '1.4rem' }, mb: 1.5, color: '#fff' }}>{item.title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.85rem', md: '0.95rem' }, lineHeight: 1.7 }}>{item.desc}</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* WHAT'S INCLUDED - Premium */}
      <Box sx={{ py: { xs: 10, md: 16 }, background: 'linear-gradient(180deg, #030303 0%, #0a0a0a 50%, #030303 100%)', position: 'relative', overflow: 'hidden' }}>
        {/* Animated lines */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #FFD700, transparent)' }} />
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.3), transparent)' }} />
        
        {/* Floating orbs */}
        <motion.div animate={{ y: [0, -20, 0], opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 4, repeat: Infinity }}>
          <Box sx={{ position: 'absolute', top: '10%', right: '10%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,215,0,0.08) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        </motion.div>
        <motion.div animate={{ y: [0, 20, 0], opacity: [0.2, 0.5, 0.2] }} transition={{ duration: 5, repeat: Infinity, delay: 1 }}>
          <Box sx={{ position: 'absolute', bottom: '20%', left: '5%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,215,0,0.06) 0%, transparent 70%)', filter: 'blur(50px)' }} />
        </motion.div>

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <Box sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 2, 
                background: 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,215,0,0.05))', 
                border: '1px solid rgba(255,215,0,0.3)',
                borderRadius: 10,
                px: 4,
                py: 1.5,
                mb: 4,
              }}>
                <CardGiftcardIcon sx={{ color: '#FFD700' }} />
                <Typography sx={{ color: '#FFD700', fontWeight: 700 }}>חבילה מושלמת</Typography>
              </Box>
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' }, fontWeight: 900, color: '#fff', mb: 2 }}>
                הכל כלול ב-<Box component="span" sx={{ 
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%)', 
                  backgroundClip: 'text', 
                  WebkitBackgroundClip: 'text', 
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 60px rgba(255,215,0,0.4)'
                }}>₪150</Box>
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '1rem', md: '1.2rem' }, maxWidth: 500, mx: 'auto' }}>
                בלי הפתעות, בלי עלויות נסתרות - מה שאתה רואה זה מה שאתה מקבל
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {included.map((item, i) => (
              <Grid item xs={6} md={4} key={i}>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8, rotateY: -30 }} 
                  whileInView={{ opacity: 1, scale: 1, rotateY: 0 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: i * 0.1, duration: 0.5 }} 
                  whileHover={{ y: -12, scale: 1.03, rotateY: 5 }}
                >
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(145deg, rgba(255,215,0,0.1) 0%, rgba(0,0,0,0.8) 100%)', 
                    border: '1px solid rgba(255,215,0,0.2)', 
                    borderRadius: 5, 
                    p: { xs: 3, md: 4 }, 
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
                    '&:hover': { 
                      border: '1px solid rgba(255,215,0,0.6)', 
                      boxShadow: '0 30px 60px rgba(255,215,0,0.2), 0 0 0 1px rgba(255,215,0,0.1)' 
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: 'linear-gradient(90deg, transparent, #FFD700, transparent)',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                    },
                    '&:hover::before': { opacity: 1 }
                  }}>
                    <Box sx={{ 
                      width: { xs: 65, md: 85 }, 
                      height: { xs: 65, md: 85 }, 
                      borderRadius: '50%', 
                      background: 'linear-gradient(135deg, rgba(255,215,0,0.25), rgba(255,215,0,0.08))', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      mx: 'auto', 
                      mb: 3,
                      boxShadow: '0 15px 35px rgba(255,215,0,0.15), inset 0 -2px 10px rgba(0,0,0,0.3)',
                      '& svg': { fontSize: { xs: 30, md: 40 }, color: '#FFD700' } 
                    }}>{item.icon}</Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', md: '1.3rem' }, mb: 1.5, color: '#fff' }}>{item.title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.85rem', md: '0.95rem' }, lineHeight: 1.7 }}>{item.desc}</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* PROCESS - Premium */}
      <Box sx={{ py: { xs: 10, md: 16 }, position: 'relative', overflow: 'hidden' }}>
        {/* Background pattern */}
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'repeating-linear-gradient(90deg, #FFD700 0px, #FFD700 1px, transparent 1px, transparent 100px), repeating-linear-gradient(0deg, #FFD700 0px, #FFD700 1px, transparent 1px, transparent 100px)' }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <Typography sx={{ color: '#FFD700', fontWeight: 700, letterSpacing: 3, mb: 2, fontSize: '0.9rem' }}>✨ התהליך שלנו ✨</Typography>
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' }, fontWeight: 900, color: '#fff' }}>
                4 צעדים ל<Box component="span" sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>דף מנצח</Box>
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {[
              { step: '01', title: 'שיחת אפיון', desc: 'נדבר על העסק שלך, הקהל, והמטרות', icon: '💬', color: '#4CAF50', gradient: 'linear-gradient(135deg, rgba(76,175,80,0.2), rgba(76,175,80,0.05))' }, 
              { step: '02', title: 'קסם קורה', desc: 'אני יוצר עיצוב וקופי מושלמים', icon: '✨', color: '#2196F3', gradient: 'linear-gradient(135deg, rgba(33,150,243,0.2), rgba(33,150,243,0.05))' }, 
              { step: '03', title: 'תיקונים', desc: 'סבב אחד עד 3 שינויים', icon: '🎯', color: '#FF9800', gradient: 'linear-gradient(135deg, rgba(255,152,0,0.2), rgba(255,152,0,0.05))' }, 
              { step: '04', title: 'באוויר!', desc: 'הדף עולה ומתחיל להביא לקוחות', icon: '🚀', color: '#FFD700', gradient: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,215,0,0.05))' }
            ].map((item, i) => (
              <Grid item xs={6} md={3} key={i}>
                <motion.div 
                  initial={{ opacity: 0, y: 60 }} 
                  whileInView={{ opacity: 1, y: 0 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: i * 0.2, duration: 0.6, type: 'spring' }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Box sx={{ textAlign: 'center', position: 'relative' }}>
                    {/* Connection line */}
                    {i < 3 && (
                      <Box sx={{ 
                        display: { xs: 'none', md: 'block' },
                        position: 'absolute', 
                        top: 50, 
                        right: -60, 
                        width: 100, 
                        height: 2, 
                        background: `linear-gradient(90deg, ${item.color}, transparent)`,
                        opacity: 0.4
                      }} />
                    )}
                    
                    <motion.div
                      animate={{ y: [0, -8, 0] }}
                      transition={{ duration: 3, repeat: Infinity, delay: i * 0.3 }}
                    >
                      <Box sx={{ 
                        width: { xs: 90, md: 110 }, 
                        height: { xs: 90, md: 110 }, 
                        borderRadius: '50%', 
                        background: item.gradient,
                        border: `3px solid ${item.color}`,
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        mx: 'auto', 
                        mb: 3,
                        boxShadow: `0 20px 40px ${item.color}33, inset 0 -5px 20px rgba(0,0,0,0.3)`,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: -6,
                          borderRadius: '50%',
                          border: `1px solid ${item.color}33`,
                        }
                      }}>
                        <Typography sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>{item.icon}</Typography>
                      </Box>
                    </motion.div>
                    
                    <Box sx={{ 
                      display: 'inline-block',
                      background: item.gradient,
                      border: `1px solid ${item.color}`,
                      borderRadius: 3,
                      px: 2,
                      py: 0.5,
                      mb: 2,
                    }}>
                      <Typography sx={{ fontSize: '0.75rem', color: item.color, fontWeight: 800, letterSpacing: 2 }}>שלב {item.step}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', md: '1.4rem' }, color: '#fff', mb: 1 }}>{item.title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.85rem', md: '0.95rem' } }}>{item.desc}</Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TESTIMONIALS - Premium */}
      <Box sx={{ py: { xs: 10, md: 16 }, background: 'linear-gradient(180deg, #050505 0%, #0a0a0a 50%, #050505 100%)', position: 'relative', overflow: 'hidden' }}>
        {/* Glowing orbs */}
        <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 6, repeat: Infinity }}>
          <Box sx={{ position: 'absolute', top: '30%', left: '-5%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,215,0,0.1) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        </motion.div>
        <motion.div animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }} transition={{ duration: 8, repeat: Infinity, delay: 2 }}>
          <Box sx={{ position: 'absolute', bottom: '20%', right: '-5%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,215,0,0.08) 0%, transparent 70%)', filter: 'blur(70px)' }} />
        </motion.div>

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                {[...Array(5)].map((_, i) => (
                  <motion.div key={i} animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}>
                    <StarIcon sx={{ color: '#FFD700', fontSize: 30 }} />
                  </motion.div>
                ))}
              </Stack>
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' }, fontWeight: 900, color: '#fff', mb: 2 }}>
                לקוחות <Box component="span" sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>אמיתיים</Box>, תוצאות <Box component="span" sx={{ background: 'linear-gradient(135deg, #4CAF50, #8BC34A)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>אמיתיות</Box>
              </Typography>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            {testimonials.map((t, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <motion.div 
                  initial={{ opacity: 0, y: 50, rotateX: -20 }} 
                  whileInView={{ opacity: 1, y: 0, rotateX: 0 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: i * 0.15, duration: 0.6 }}
                  whileHover={{ y: -15, scale: 1.02 }}
                >
                  <Card sx={{ 
                    height: '100%', 
                    background: 'linear-gradient(160deg, rgba(255,215,0,0.12) 0%, rgba(0,0,0,0.9) 100%)', 
                    border: '1px solid rgba(255,215,0,0.2)', 
                    borderRadius: 5, 
                    p: { xs: 3, md: 4 }, 
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      border: '1px solid rgba(255,215,0,0.5)',
                      boxShadow: '0 30px 60px rgba(255,215,0,0.15)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: 'linear-gradient(90deg, #FFD700, #FFA500)',
                    }
                  }}>
                    {/* Quote icon */}
                    <FormatQuoteIcon sx={{ 
                      position: 'absolute', 
                      top: 20, 
                      right: 20, 
                      fontSize: 50, 
                      color: 'rgba(255,215,0,0.1)',
                      transform: 'rotate(180deg)'
                    }} />
                    
                    {/* Avatar */}
                    <Box sx={{ 
                      width: 70, 
                      height: 70, 
                      borderRadius: '50%', 
                      background: 'linear-gradient(135deg, #FFD700, #FFA500)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      mb: 3, 
                      fontSize: '2rem',
                      boxShadow: '0 10px 30px rgba(255,215,0,0.3)',
                      border: '3px solid rgba(255,255,255,0.2)'
                    }}>{t.image}</Box>
                    
                    {/* Stars */}
                    <Stack direction="row" spacing={0.5} sx={{ mb: 3 }}>
                      {[...Array(t.rating)].map((_, j) => (
                        <StarIcon key={j} sx={{ color: '#FFD700', fontSize: 20 }} />
                      ))}
                    </Stack>
                    
                    {/* Quote */}
                    <Typography sx={{ 
                      color: 'rgba(255,255,255,0.85)', 
                      mb: 3, 
                      fontSize: { xs: '0.9rem', md: '1rem' }, 
                      lineHeight: 1.8,
                      fontStyle: 'italic'
                    }}>"{t.text}"</Typography>
                    
                    {/* Author */}
                    <Box sx={{ borderTop: '1px solid rgba(255,215,0,0.2)', pt: 2, mt: 'auto' }}>
                      <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1.1rem' }}>{t.name}</Typography>
                      <Typography sx={{ color: '#FFD700', fontSize: '0.85rem', fontWeight: 600 }}>{t.business}</Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ADDONS - Premium */}
      <Box sx={{ py: { xs: 10, md: 16 }, position: 'relative', overflow: 'hidden' }}>
        {/* Background grid pattern */}
        <Box sx={{ 
          position: 'absolute', 
          inset: 0, 
          opacity: 0.02,
          backgroundImage: `radial-gradient(circle at 1px 1px, #FFD700 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                <Typography sx={{ fontSize: { xs: '3rem', md: '4rem' }, mb: 2 }}>🛒</Typography>
              </motion.div>
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' }, fontWeight: 900, color: '#fff', mb: 2 }}>
                צריך <Box component="span" sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>עוד משהו?</Box>
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '1rem', md: '1.2rem' } }}>תוספות אופציונליות במחירים הוגנים</Typography>
            </Box>
          </motion.div>

          <Grid container spacing={3}>
            {addons.map((a, i) => (
              <Grid item xs={6} md={4} key={i}>
                <motion.div 
                  initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }} 
                  whileInView={{ opacity: 1, x: 0 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.05, x: 5 }}
                >
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(0,0,0,0.4))', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    borderRadius: 4, 
                    p: { xs: 2.5, md: 3 }, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2.5, 
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': { 
                      background: 'linear-gradient(135deg, rgba(255,215,0,0.1), rgba(0,0,0,0.6))', 
                      border: '1px solid rgba(255,215,0,0.4)',
                      boxShadow: '0 15px 40px rgba(255,215,0,0.15)'
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      background: 'linear-gradient(180deg, #FFD700, #FFA500)',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                    },
                    '&:hover::before': { opacity: 1 }
                  }}>
                    <Box sx={{ 
                      width: 55, 
                      height: 55, 
                      borderRadius: 3, 
                      background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,215,0,0.05))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      '& svg': { fontSize: { xs: 24, md: 28 }, color: '#FFD700' } 
                    }}>{a.icon}</Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', md: '1.1rem' }, color: '#fff', mb: 0.5 }}>{a.name}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>{a.desc}</Typography>
                    </Box>
                    <Chip 
                      label={`₪${a.price}`} 
                      sx={{ 
                        background: 'linear-gradient(135deg, #FFD700, #FFA500)', 
                        color: '#000', 
                        fontWeight: 800, 
                        fontSize: { xs: '0.85rem', md: '0.95rem' },
                        px: 1,
                        boxShadow: '0 5px 15px rgba(255,215,0,0.3)'
                      }} 
                    />
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* PORTFOLIO - Premium */}
      <Box sx={{ py: { xs: 10, md: 16 }, background: 'linear-gradient(180deg, #030303 0%, #0a0a0a 100%)', position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #FFD700, transparent)' }} />
        
        <Container maxWidth="md">
          <motion.div 
            initial={{ opacity: 0, y: 50, scale: 0.95 }} 
            whileInView={{ opacity: 1, y: 0, scale: 1 }} 
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Card sx={{ 
              background: 'linear-gradient(145deg, rgba(255,215,0,0.15) 0%, rgba(0,0,0,0.9) 100%)', 
              border: '2px solid rgba(255,215,0,0.4)', 
              borderRadius: 6, 
              cursor: 'pointer', 
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)', 
              overflow: 'hidden',
              position: 'relative',
              '&:hover': { 
                transform: 'scale(1.03) translateY(-10px)', 
                boxShadow: '0 40px 100px rgba(255,215,0,0.3), 0 0 0 1px rgba(255,215,0,0.2)' 
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.1), transparent)',
                transition: 'left 0.8s',
              },
              '&:hover::before': { left: '100%' }
            }} onClick={() => window.open('https://diamond-algo.com', '_blank')}>
              <Box sx={{ p: { xs: 5, md: 8 }, textAlign: 'center', position: 'relative', zIndex: 1 }}>
                <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                  <Box sx={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1.5,
                    background: 'linear-gradient(135deg, rgba(76,175,80,0.2), rgba(76,175,80,0.05))',
                    border: '1px solid rgba(76,175,80,0.5)',
                    borderRadius: 10,
                    px: 3,
                    py: 1,
                    mb: 4,
                  }}>
                    <VerifiedIcon sx={{ color: '#4CAF50' }} />
                    <Typography sx={{ color: '#4CAF50', fontWeight: 700 }}>פרויקט אמיתי ופעיל</Typography>
                  </Box>
                </motion.div>
                
                <Typography variant="h3" sx={{ fontWeight: 900, mb: 2, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                  <Box component="span" sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>💎 Diamond Algo</Box>
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, fontSize: { xs: '1rem', md: '1.2rem' }, maxWidth: 500, mx: 'auto' }}>
                  אתר מלא לבוט מסחר אוטומטי - עיצוב פרימיום, אזור לקוחות, ותרגום לאנגלית
                </Typography>
                
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ 
                    background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                    color: '#000',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    px: 5,
                    py: 1.5,
                    borderRadius: 3,
                    boxShadow: '0 10px 30px rgba(255,215,0,0.3)',
                    '&:hover': { 
                      background: 'linear-gradient(135deg, #FFE44D, #FFB732)',
                      transform: 'scale(1.05)'
                    } 
                  }}
                >
                  צפה באתר החי →
                </Button>
              </Box>
            </Card>
          </motion.div>
        </Container>
      </Box>

      {/* FAQ - Premium */}
      <Box sx={{ py: { xs: 10, md: 16 }, position: 'relative', overflow: 'hidden' }}>
        {/* Background decoration */}
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,215,0,0.03) 0%, transparent 70%)', filter: 'blur(80px)' }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <motion.div animate={{ rotateY: [0, 360] }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}>
                <Typography sx={{ fontSize: { xs: '3rem', md: '4rem' }, mb: 2 }}>💡</Typography>
              </motion.div>
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' }, fontWeight: 900, color: '#fff', mb: 2 }}>
                יש שאלות? <Box component="span" sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>יש תשובות!</Box>
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem' }}>כל מה שצריך לדעת לפני שמתחילים</Typography>
            </Box>
          </motion.div>

          <Grid container spacing={3}>
            {faqs.map((f, i) => (
              <Grid item xs={12} key={i}>
                <motion.div 
                  initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50, rotateY: i % 2 === 0 ? -10 : 10 }} 
                  whileInView={{ opacity: 1, x: 0, rotateY: 0 }} 
                  viewport={{ once: true }} 
                  transition={{ delay: i * 0.1, duration: 0.6, type: 'spring' }}
                >
                  <Accordion sx={{ 
                    background: 'linear-gradient(135deg, rgba(255,215,0,0.05), rgba(0,0,0,0.6))', 
                    border: '1px solid rgba(255,215,0,0.15)', 
                    borderRadius: '24px !important', 
                    mb: 2,
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:before': { display: 'none' }, 
                    '&.Mui-expanded': { 
                      background: 'linear-gradient(135deg, rgba(255,215,0,0.12), rgba(0,0,0,0.8))', 
                      border: '1px solid rgba(255,215,0,0.4)',
                      boxShadow: '0 20px 50px rgba(255,215,0,0.15)',
                      transform: 'scale(1.02)',
                    },
                    '&:hover': {
                      border: '1px solid rgba(255,215,0,0.3)',
                      transform: 'translateX(5px)',
                    }
                  }}>
                    <AccordionSummary 
                      expandIcon={
                        <motion.div whileHover={{ rotate: 180 }} transition={{ duration: 0.3 }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: '50%', 
                            background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,215,0,0.05))',
                            border: '1px solid rgba(255,215,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <ExpandMoreIcon sx={{ color: '#FFD700' }} />
                          </Box>
                        </motion.div>
                      } 
                      sx={{ py: 2.5, px: 4 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ 
                          width: 45, 
                          height: 45, 
                          borderRadius: 3, 
                          background: 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,215,0,0.05))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <Typography sx={{ fontWeight: 800, color: '#FFD700' }}>{String(i + 1).padStart(2, '0')}</Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: { xs: '1rem', md: '1.2rem' } }}>{f.q}</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 4, pb: 4, pt: 0 }}>
                      <Box sx={{ 
                        background: 'rgba(255,215,0,0.03)', 
                        borderRadius: 3, 
                        p: 3,
                        borderLeft: '3px solid #FFD700',
                      }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 2, fontSize: { xs: '0.95rem', md: '1.05rem' } }}>{f.a}</Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* QUALITY PROMISE SECTION */}
      <Box sx={{ py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(33,150,243,0.1) 0%, transparent 50%, rgba(255,215,0,0.1) 100%)' }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            whileInView={{ opacity: 1, scale: 1 }} 
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: 'spring' }}
          >
            <Card sx={{ 
              background: 'linear-gradient(135deg, rgba(33,150,243,0.15) 0%, rgba(0,0,0,0.9) 100%)', 
              border: '2px solid rgba(33,150,243,0.4)', 
              borderRadius: 6, 
              p: { xs: 4, md: 6 },
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Icon animation */}
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, opacity: 0.1 }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}>
                  <WorkspacePremiumIcon sx={{ fontSize: 200, color: '#2196F3' }} />
                </motion.div>
              </Box>
              
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                  <motion.div animate={{ scale: [1, 1.1, 1], rotateY: [0, 10, -10, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                    <Box sx={{ 
                      width: 120, 
                      height: 120, 
                      borderRadius: '50%', 
                      background: 'linear-gradient(135deg, #2196F3, #64B5F6)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mx: 'auto',
                      boxShadow: '0 20px 60px rgba(33,150,243,0.4)',
                    }}>
                      <WorkspacePremiumIcon sx={{ fontSize: 60, color: '#fff' }} />
                    </Box>
                  </motion.div>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="h3" sx={{ fontWeight: 900, mb: 2, color: '#fff', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                    <Box component="span" sx={{ color: '#2196F3' }}>מקצועיות</Box> שמדברת בעד עצמה
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.8, mb: 3 }}>
                    5 שנות ניסיון, מאות דפי נחיתה, ולקוחות שחוזרים שוב ושוב. 
                    אני מתמקד באיכות ובתוצאות - כל דף נבנה בקפידה כדי להביא לך לקוחות חדשים.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    {['5 שנות ניסיון', 'אספקה ב-48 שעות', 'תמיכה מלאה'].map((item, i) => (
                      <motion.div key={i} whileHover={{ scale: 1.05 }}>
                        <Chip 
                          icon={<CheckCircleIcon sx={{ color: '#2196F3 !important' }} />}
                          label={item} 
                          sx={{ 
                            background: 'rgba(33,150,243,0.15)', 
                            border: '1px solid rgba(33,150,243,0.4)',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            py: 2.5,
                            px: 1,
                          }} 
                        />
                      </motion.div>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </Card>
          </motion.div>
        </Container>
      </Box>

      {/* FINAL CTA - Dramatic */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ 
          position: 'absolute', 
          inset: 0, 
          background: 'radial-gradient(ellipse at center, rgba(255,215,0,0.15) 0%, transparent 70%)',
        }} />
        <motion.div 
          style={{ position: 'absolute', inset: 0 }}
          animate={{ 
            background: [
              'radial-gradient(circle at 20% 50%, rgba(255,215,0,0.1) 0%, transparent 50%)',
              'radial-gradient(circle at 80% 50%, rgba(255,215,0,0.1) 0%, transparent 50%)',
              'radial-gradient(circle at 20% 50%, rgba(255,215,0,0.1) 0%, transparent 50%)',
            ]
          }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div 
            initial={{ opacity: 0, y: 50 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                <Typography sx={{ fontSize: { xs: '4rem', md: '5rem' }, mb: 2 }}>🔥</Typography>
              </motion.div>
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', sm: '3rem', md: '3.5rem' }, fontWeight: 900, color: '#fff', mb: 2 }}>
                מה אתה מחכה?
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '1.1rem', md: '1.3rem' }, mb: 5, maxWidth: 600, mx: 'auto' }}>
                בעוד שאתה מתלבט, המתחרים שלך כבר מקבלים לידים.
                <Box component="span" sx={{ color: '#FFD700', fontWeight: 700 }}> הזמן לפעול הוא עכשיו.</Box>
              </Typography>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center" alignItems="center">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    sx={{ 
                      background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                      color: '#000',
                      fontWeight: 900,
                      fontSize: { xs: '1.2rem', md: '1.4rem' },
                      px: { xs: 4, md: 6 },
                      py: 2,
                      borderRadius: 4,
                      boxShadow: '0 20px 50px rgba(255,215,0,0.4)',
                      '&:hover': { 
                        background: 'linear-gradient(135deg, #FFE44D, #FFB732)',
                      } 
                    }}
                  >
                    🚀 אני רוצה דף נחיתה מנצח!
                  </Button>
                </motion.div>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>או</Typography>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Button 
                    variant="outlined" 
                    size="large"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => handleWhatsApp()}
                    sx={{ 
                      borderColor: '#25D366',
                      borderWidth: 2,
                      color: '#25D366',
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      '&:hover': { 
                        background: 'rgba(37,211,102,0.1)',
                        borderColor: '#25D366',
                        borderWidth: 2,
                      } 
                    }}
                  >
                    דבר איתי עכשיו
                  </Button>
                </motion.div>
              </Stack>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* CONTACT FORM - Premium */}
      <Box id="contact-form" sx={{ py: { xs: 12, md: 18 }, background: 'linear-gradient(180deg, #030303 0%, #0a0a0a 50%, #030303 100%)', position: 'relative', overflow: 'hidden' }}>
        {/* Animated background elements */}
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}>
          <Box sx={{ position: 'absolute', top: '-20%', left: '-10%', width: 500, height: 500, border: '1px solid rgba(255,215,0,0.05)', borderRadius: '50%' }} />
        </motion.div>
        <motion.div animate={{ rotate: -360 }} transition={{ duration: 70, repeat: Infinity, ease: 'linear' }}>
          <Box sx={{ position: 'absolute', bottom: '-30%', right: '-15%', width: 600, height: 600, border: '1px solid rgba(255,215,0,0.03)', borderRadius: '50%' }} />
        </motion.div>
        <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(255,215,0,0.1) 0%, transparent 60%)' }} />
        
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div 
            initial={{ opacity: 0, y: 50, scale: 0.95 }} 
            whileInView={{ opacity: 1, y: 0, scale: 1 }} 
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Card sx={{ 
              background: 'linear-gradient(160deg, rgba(255,215,0,0.15) 0%, rgba(0,0,0,0.95) 100%)', 
              border: '2px solid rgba(255,215,0,0.4)', 
              borderRadius: 6, 
              p: { xs: 4, md: 6 }, 
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 40px 100px rgba(255,215,0,0.15)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(90deg, #FFD700, #FFA500, #FFD700)',
              }
            }}>
              <motion.div animate={{ y: [0, -10, 0], rotate: [0, 5, -5, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                <EmojiEventsIcon sx={{ fontSize: 80, color: '#FFD700', mb: 2, filter: 'drop-shadow(0 10px 30px rgba(255,215,0,0.4))' }} />
              </motion.div>
              
              <Typography variant="h3" sx={{ fontWeight: 900, mb: 1, color: '#fff', fontSize: { xs: '1.8rem', md: '2.3rem' } }}>
                מוכן ל<Box component="span" sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>הגדיל</Box> את העסק?
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 5, fontSize: '1.1rem' }}>השאר פרטים ואחזור אליך תוך דקות ⚡</Typography>

              {showSuccess ? (
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                  <Alert icon={<CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 30 }} />} sx={{ background: 'linear-gradient(135deg, rgba(76,175,80,0.15), rgba(76,175,80,0.05))', border: '2px solid rgba(76,175,80,0.4)', color: '#fff', borderRadius: 4, p: 3 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', mb: 1 }}>🎉 מעולה! קיבלתי את הפרטים</Typography>
                    <Typography sx={{ fontSize: '1rem', opacity: 0.9 }}>אחזור אליך בהקדם האפשרי!</Typography>
                  </Alert>
                </motion.div>
              ) : (
                <Box component="form" onSubmit={handleFormspreeSubmit}>
                  <Stack spacing={3}>
                    <TextField fullWidth name="name" placeholder="השם שלך" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.05)', borderRadius: 3, fontSize: '1.1rem', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)', borderWidth: 2 }, '&:hover fieldset': { borderColor: 'rgba(255,215,0,0.5)' }, '&.Mui-focused fieldset': { borderColor: '#FFD700' } }, '& input': { color: '#fff', py: 2 }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)' } }} />
                    <TextField fullWidth name="phone" placeholder="טלפון" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.05)', borderRadius: 3, fontSize: '1.1rem', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)', borderWidth: 2 }, '&:hover fieldset': { borderColor: 'rgba(255,215,0,0.5)' }, '&.Mui-focused fieldset': { borderColor: '#FFD700' } }, '& input': { color: '#fff', py: 2 }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)' } }} />
                    <TextField fullWidth name="business" placeholder="ספר לי קצת על העסק שלך..." value={formData.business} onChange={(e) => setFormData({ ...formData, business: e.target.value })} multiline rows={4} sx={{ '& .MuiOutlinedInput-root': { background: 'rgba(255,255,255,0.05)', borderRadius: 3, fontSize: '1.05rem', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)', borderWidth: 2 }, '&:hover fieldset': { borderColor: 'rgba(255,215,0,0.5)' }, '&.Mui-focused fieldset': { borderColor: '#FFD700' } }, '& textarea': { color: '#fff' }, '& textarea::placeholder': { color: 'rgba(255,255,255,0.5)' } }} />
                    <input type="hidden" name="_subject" value="ליד חדש מ-Diamond Landing! 💎" />
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button type="submit" variant="contained" size="large" fullWidth disabled={formspreeState.submitting} sx={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#000', py: 2.5, fontSize: '1.3rem', fontWeight: 900, borderRadius: 4, boxShadow: '0 15px 40px rgba(255,215,0,0.3)', '&:hover': { background: 'linear-gradient(135deg, #FFE44D 0%, #FFB732 100%)' }, '&.Mui-disabled': { background: 'rgba(255,215,0,0.3)', color: 'rgba(0,0,0,0.5)' } }}>
                        {formspreeState.submitting ? <CircularProgress size={28} sx={{ color: '#000' }} /> : '🚀 בואו נתחיל!'}
                      </Button>
                    </motion.div>
                  </Stack>
                </Box>
              )}

              <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.15)' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 3, fontSize: '1rem' }}>מעדיף לדבר ישירות?</Typography>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Button 
                  variant="outlined" 
                  size="large"
                  startIcon={<WhatsAppIcon />} 
                  onClick={() => handleWhatsApp()} 
                  sx={{ 
                    borderColor: '#25D366', 
                    color: '#25D366', 
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    borderWidth: 2,
                    borderRadius: 3,
                    '&:hover': { 
                      background: 'rgba(37,211,102,0.1)', 
                      borderColor: '#25D366',
                      borderWidth: 2,
                    } 
                  }}
                >
                  058-748-9062
                </Button>
              </motion.div>
            </Card>
          </motion.div>
        </Container>
      </Box>

      {/* FOOTER - Premium */}
      <Box sx={{ py: 8, background: '#000', borderTop: '1px solid rgba(255,215,0,0.2)', position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.5), transparent)' }} />
        
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <motion.div whileHover={{ scale: 1.02 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '1.8rem' }}>💎</Typography>
                  <Typography sx={{ fontWeight: 900, fontSize: '1.4rem' }}>
                    <Box component="span" sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Diamond Landing</Box>
                  </Typography>
                </Stack>
              </motion.div>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem' }}>דפי נחיתה שמביאים תוצאות אמיתיות</Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'center' } }}>
              <Stack direction="row" spacing={4} justifyContent={{ xs: 'flex-start', md: 'center' }}>
                <Typography onClick={() => setTosOpen(true)} sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { color: '#FFD700' } }}>תקנון</Typography>
                <Typography onClick={() => setAccessibilityOpen(true)} sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.95rem', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { color: '#FFD700' } }}>נגישות</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>© 2026 Diamond Landing | כל הזכויות שמורות</Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
